
import CurrentVisits from "./CurrentVisits.vue";
import DraftVisits from "./DraftVisits.vue";
import CompletedVisits from "./CompletedVisits.vue";
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import GET_ACTIVITIES from "@/apollo/projectActivities/GetActivities.gql";
import ActivityFormModal from "@/components/projects/activities/ActivityFormModalNew.vue";
import PageSectionHeading from "@/components/page/page-section-heading.vue";
import UiButton from "@/components/button/ui-button.vue";
import EmptyListPlaceholder from "@/components/common/EmptyListPlaceholderNew.vue";
import LoadingOverlay from "@/components/common/LoadingOverlay.vue";
import RadioButtonGroup from "@/components/common/RadioButtonGroupNew.vue";
import PlusIcon from "@/components/icons/PlusIcon.vue";
import CardPagination from "@/components/card/PaginationNew.vue";
import FormGroup from "@/components/form/FormGroupNew.vue";
import ManagementActivityCard from "@/components/projects/activities/ManagementActivityCardNew.vue";
import { Project, Project_Activity, User } from "@/generated/graphql";
import ActivityCustomRequest from "@/components/projects/activities/ActivityCustomRequest.vue";
import ContractorsMultiselect from "@/components/autocompleteMultiselects/ContractorsMultiselectNew.vue";
import TableHeaders from "@/views/manage/projectDetails/ActivityHeaders.vue";
import moment from "moment";

@Component({
  components: {
    ActivityCustomRequest,
    ContractorsMultiselect,
    ManagementActivityCard,
    UiButton,
    FormGroup,
    ActivityFormModal,
    PageSectionHeading,
    EmptyListPlaceholder,
    CardPagination,
    TableHeaders,
    LoadingOverlay,
    RadioButtonGroup,
    PlusIcon,
    CurrentVisits,
    DraftVisits,
    CompletedVisits,
  },
  apollo: {
    activities: {
      query: GET_ACTIVITIES,
      variables() {
        return this.activitiesArgs;
      },
      update(data) {
        localStorage.setItem("totalActivites", data.project_activity.length);
        return data.project_activity;
      },
    },
    activitiesDrafts: {
      query: GET_ACTIVITIES,
      variables() {
        return this.getActivitiesArgsDrafts;
      },
      update(data) {
        return data.project_activity;
      },
    },
    activitiesCurrent: {
      query: GET_ACTIVITIES,
      variables() {
        return this.getActivitiesArgsCurrent;
      },
      update(data) {
        return Array.isArray(data.project_activity)
          ? data.project_activity
          : [];
      },
    },
    activitiesCompleted: {
      query: GET_ACTIVITIES,
      variables() {
        return this.getActivitiesArgsCompleted;
      },
      update(data) {
        return data.project_activity;
      },
    },
  },
})
export default class Activities extends Vue {
  @Prop({ type: Object, required: true }) readonly project!: Project;
  @Prop({ type: String, required: true }) readonly userRole!: string;
  @Prop({ type: Object, required: true }) readonly currentUser!: User;
  @Prop({ type: String, required: true }) readonly companyId!: string;

  listFilteringCurrentCondition: string = "All"; // Default to "All"
  activityModalOpened: boolean = false;

  // Apollo Data
  activitiesCurrent: any[] = [];
  activitiesDrafts: any[] = [];
  activitiesCompleted: any[] = [];

  // List of filtering conditions for the RadioButtonGroup
  listFilteringConditions() {
    return [
      {
        value: "All",
        label: "All",
        total: localStorage.getItem("totalActivites"),
      },
      {
        value: "Current",
        label: "Current",
        total: this.activitiesCurrent.length,
      },
      {
        value: "Completed",
        label: "Completed",
        total: this.activitiesCompleted.length,
      },
      {
        value: "Draft",
        label: "Draft",
        total: this.activitiesDrafts.length,
      },
    ];
  }
  get activitiesArgs() {
    return {
      where: {
        _and: [
          { project_id: { _eq: this.$route.params.id } },
          { deleted: { _eq: false } },
        ],
      },
    };
  }

  // Arguments for Apollo Queries
  get getActivitiesArgsCurrent() {
        // Use current UTC time minus 24 hours for the filter
    const nowUtc = this.$moment.utc().toISOString();
    return {
      where: {
        _and: [
          { project_id: { _eq: this.$route.params.id } },
          { deleted: { _eq: false } },
          { completed: { _eq: false } },
          { drafted: { _eq: false } },
           {
            _or: [
              { checkin_status: { _is_null: false } },
              { end_time: { _gt: nowUtc } }
            ]
          }
        ],
      },
    };
  }

  get getActivitiesArgsDrafts() {
    return {
      where: {
        _and: [
          { project_id: { _eq: this.$route.params.id } },
          { deleted: { _eq: false } },
          { completed: { _eq: false } },
          { drafted: { _eq: true } },
        ],
      },
    };
  }

  get getActivitiesArgsCompleted() {
    const expiredTime = moment.utc().subtract(5, "minutes").toISOString();
    return {
      where: {
        _and: [
          { project_id: { _eq: this.$route.params.id } },
          { deleted: { _eq: false } },
          {
            _or: [
              { completed: { _eq: true } },
              {
                _and: [
                  { checkin_status: { _is_null: true } },
                  { end_time: { _lte: expiredTime } }
                ]
              }
            ]
          },
          { drafted: { _eq: false } },
        ],
      },
    };
  }

  // Handle activity list changes
  onActivityListItemsChanged() {
    this.$apollo.queries.activitiesCurrent.refetch();
    this.$apollo.queries.activitiesDrafts.refetch();
    this.$apollo.queries.activitiesCompleted.refetch();
  }

  // Handle activity addition
  onActivityListItemAdded() {
    this.onActivityListItemsChanged();
    this.closeActivityModal();
  }

  // Open the activity modal
  openActivityModal() {
    this.activityModalOpened = true;
  }

  // Close the activity modal
  closeActivityModal() {
    this.activityModalOpened = false;
  }

  // Check if the project is completed
  get projectCompleted(): boolean {
    return this.project?.completed;
  }

  // Check if activities are still loading
  get isLoading(): boolean {
    return (
      this.$apollo.queries.activitiesCurrent.loading ||
      this.$apollo.queries.activitiesDrafts.loading ||
      this.$apollo.queries.activitiesCompleted.loading
    );
  }

  // Check if activities are loaded
  get isActivitiesLoaded(): boolean {
    return (
      this.isLoading &&
      !this.activitiesCurrent &&
      !this.activitiesDrafts &&
      !this.activitiesCompleted
    );
  }

  isStartTimePassed(activity: Project_Activity): boolean {
    if (activity.checkin_status) {
      return false;
    }
    // Convert start time (UTC) to local time
    const startTimeLocal = this.$moment.utc(activity.start_time).local();
    // Add 24 hours to the start time
    const startTimePlus24Hours = startTimeLocal.clone().add(24, 'hours');
    // Get current local time
    const now = this.$moment();
    // Check if current time is after (start time + 24 hours)
    return now.isAfter(startTimePlus24Hours);
  }

  get displayedActivitiesCompleted(): Project_Activity[] {
    // Show activities that are completed OR start_time > 24h ago
    return this.activitiesCompleted.filter(activity => {
      return activity.completed || this.isStartTimePassed(activity);
    });
  }
}
